import { useState, useEffect } from "react";
import { FileX, Download, Search, Receipt, Calendar, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreditNotes({ user }) {
  const [creditNotes, setCreditNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  // Filter credit notes based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredNotes(creditNotes);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = creditNotes.filter(cn => 
        cn.credit_note_number?.toLowerCase().includes(term) ||
        cn.invoice_number?.toLowerCase().includes(term) ||
        cn.customer_name?.toLowerCase().includes(term) ||
        cn.customer_fiscal_code?.toLowerCase().includes(term)
      );
      setFilteredNotes(filtered);
    }
  }, [searchTerm, creditNotes]);

  const fetchCreditNotes = async () => {
    try {
      const response = await authFetch(`${API}/credit-notes`, { });
      if (response.ok) {
        const data = await response.json();
        setCreditNotes(data);
        setFilteredNotes(data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const downloadXML = async (creditNoteId, creditNoteNumber) => {
    try {
      const response = await authFetch(`${API}/credit-notes/${creditNoteId}/xml`, {
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${creditNoteNumber.replace("/", "-")}.xml`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("XML scaricato");
      }
    } catch (error) {
      toast.error("Errore nel download");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="credit-notes-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Archivio Note di Credito</h1>
        <p className="text-zinc-500">Registro delle note di credito elettroniche emesse</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kpi-card">
          <div className="flex items-center gap-3">
            <FileX className="w-8 h-8 text-orange-400" />
            <div>
              <div className="kpi-value">{creditNotes.length}</div>
              <div className="kpi-label">Note Emesse</div>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-3">
            <Receipt className="w-8 h-8 text-primary" />
            <div>
              <div className="kpi-value font-['JetBrains_Mono']">
                € {creditNotes.reduce((sum, cn) => sum + (cn.total || 0), 0).toFixed(2)}
              </div>
              <div className="kpi-label">Totale Stornato</div>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-3">
            <Calendar className="w-8 h-8 text-blue-400" />
            <div>
              <div className="kpi-value">
                {creditNotes.filter(cn => {
                  const cnDate = new Date(cn.created_at);
                  const now = new Date();
                  return cnDate.getMonth() === now.getMonth() && cnDate.getFullYear() === now.getFullYear();
                }).length}
              </div>
              <div className="kpi-label">Questo Mese</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Cerca per numero nota, fattura, cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="search-credit-notes"
        />
      </div>

      {/* Credit Notes List */}
      {filteredNotes.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Numero NC</th>
                <th>Data</th>
                <th>Fattura Collegata</th>
                <th>Cliente</th>
                <th>Codice Fiscale</th>
                <th className="text-right">Importo</th>
                <th>Motivazione</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotes.map((cn) => (
                <tr key={cn.credit_note_id}>
                  <td className="font-['JetBrains_Mono'] text-orange-400 font-bold">
                    {cn.credit_note_number}
                  </td>
                  <td className="font-['JetBrains_Mono']">
                    {formatDate(cn.created_at)}
                  </td>
                  <td>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-blue-500/20 text-blue-400">
                      <Receipt className="w-3 h-3" />
                      #{cn.invoice_number}
                    </span>
                  </td>
                  <td className="flex items-center gap-2">
                    <User className="w-4 h-4 text-zinc-500" />
                    {cn.customer_name}
                  </td>
                  <td className="font-['JetBrains_Mono'] text-sm text-zinc-400">
                    {cn.customer_fiscal_code}
                  </td>
                  <td className="text-right font-['JetBrains_Mono'] font-bold text-orange-400">
                    € {(cn.total || 0).toFixed(2)}
                  </td>
                  <td className="max-w-xs truncate text-zinc-400 text-sm" title={cn.reason}>
                    {cn.reason || "-"}
                  </td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadXML(cn.credit_note_id, cn.credit_note_number)}
                      className="text-orange-500 hover:text-orange-400"
                      data-testid="download-credit-note-xml"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      XML
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <FileX className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">{searchTerm ? "Nessun risultato" : "Nessuna nota di credito"}</p>
          <p className="empty-state-text">
            {searchTerm 
              ? "Prova con altri termini di ricerca" 
              : "Le note di credito vengono create dalla pagina Fatture"
            }
          </p>
        </div>
      )}
    </div>
  );
}
